import { Directive, ElementRef, EventEmitter, HostListener, Input, Output } from '@angular/core';
import {
  AstralTemporalInputKind,
  compactAstralTemporalInputLength,
  normalizeAstralTemporalInput,
} from './astral-temporal-input.util';

const COMPACT_INPUT_TIMEOUT_MS = 2000;

@Directive({
  selector: 'input[astralTemporalInput]',
  standalone: true,
})
export class AstralTemporalInputDirective {
  @Input({ required: true }) astralTemporalInput: AstralTemporalInputKind;
  @Output() readonly astralTemporalInputCommit = new EventEmitter<string>();

  private compactInput = '';
  private lastDigitAt = 0;

  constructor(private readonly elementRef: ElementRef<HTMLInputElement>) {}

  @HostListener('keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent): void {
    if (event.ctrlKey || event.metaKey || event.altKey || !/^\d$/.test(event.key)) {
      if (!['Shift', 'Control', 'Meta', 'Alt'].includes(event.key)) {
        this.resetCompactInput();
      }
      return;
    }

    const now = Date.now();
    if (now - this.lastDigitAt > COMPACT_INPUT_TIMEOUT_MS) {
      this.compactInput = '';
    }

    this.lastDigitAt = now;
    this.compactInput += event.key;

    if (this.compactInput.length !== compactAstralTemporalInputLength(this.astralTemporalInput)) {
      return;
    }

    const normalized = normalizeAstralTemporalInput(this.compactInput, this.astralTemporalInput);
    this.resetCompactInput();
    if (!normalized) {
      return;
    }

    event.preventDefault();
    this.commit(normalized);
  }

  @HostListener('paste', ['$event'])
  handlePaste(event: ClipboardEvent): void {
    const pastedValue = event.clipboardData?.getData('text');
    if (!pastedValue) {
      return;
    }

    const normalized = normalizeAstralTemporalInput(pastedValue, this.astralTemporalInput);
    if (!normalized) {
      return;
    }

    event.preventDefault();
    this.resetCompactInput();
    this.commit(normalized);
  }

  @HostListener('copy', ['$event'])
  handleCopy(event: ClipboardEvent): void {
    const value = this.elementRef.nativeElement.value;
    if (!value || !event.clipboardData) {
      return;
    }

    // Native temporal inputs are segmented differently by each browser. Copy
    // their canonical value so selecting the control always yields one value.
    event.preventDefault();
    event.clipboardData.setData('text/plain', value);
  }

  @HostListener('blur')
  resetCompactInput(): void {
    this.compactInput = '';
    this.lastDigitAt = 0;
  }

  private commit(value: string): void {
    this.elementRef.nativeElement.value = value;
    this.astralTemporalInputCommit.emit(value);
  }
}
